/*
 * Карточка «Узлы» на странице кластера: узлы из Kubernetes API кластера (плагин Kubernetes,
 * прокси бэкенда), сгруппированные в пулы по метке (по умолчанию viva.io/pool — её пишет
 * шаблон k8s-cluster). Строка-заголовок пула: узлы, суммарные vCPU/память/GPU, тип
 * инстанса, зона, готовность. Строка узла: имя, ОС и kubelet, адреса с копированием,
 * статус, роли и taints, запросы CPU/памяти относительно allocatable, поды.
 * Запросы — сумма requests подов на узле (Pending/Running).
 */
import { useMemo, useState } from 'react';
import {
  IconButton,
  InputAdornment,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  makeStyles,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined';
import SearchIcon from '@material-ui/icons/Search';
import {
  EmptyState,
  InfoCard,
  Link,
  Progress,
  ResponseErrorPanel,
  StatusError,
  StatusOK,
  StatusWarning,
} from '@backstage/core-components';
import { useEntity } from '@backstage/plugin-catalog-react';
import { CLUSTER_DOMAIN_ANNOTATION, annotation } from '../../../lib/viva';
import {
  NodePool,
  NodeRow,
  POOL_LABEL,
  availableGroupLabels,
  formatCores,
  formatGiB,
  groupNodes,
  matchesQuery,
} from '../../../lib/k8s';
import { KubernetesIcon } from '../../icons';
import { NOT_CONNECTED, useClusterData } from './useClusterData';

const useStyles = makeStyles(theme => ({
  tools: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'center',
    flexWrap: 'wrap',
    marginBottom: theme.spacing(1.5),
  },
  poolRow: {
    background: theme.palette.action.hover,
    cursor: 'pointer',
    '& td': { paddingTop: 6, paddingBottom: 6 },
  },
  poolName: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    fontWeight: 800,
    whiteSpace: 'nowrap',
  },
  facts: {
    display: 'inline-flex',
    gap: theme.spacing(2),
    marginLeft: theme.spacing(2),
    color: theme.palette.text.secondary,
    fontSize: 13,
    flexWrap: 'wrap',
  },
  factsValue: { color: theme.palette.text.primary, fontWeight: 700 },
  tag: {
    display: 'inline-flex',
    alignItems: 'center',
    height: 22,
    padding: '0 9px',
    borderRadius: 999,
    background: theme.palette.action.selected,
    color: theme.palette.text.secondary,
    fontSize: 11.5,
    fontWeight: 600,
    whiteSpace: 'nowrap',
  },
  nodeName: { fontWeight: 700, whiteSpace: 'nowrap' },
  nodeSub: {
    display: 'block',
    fontSize: 12,
    color: theme.palette.text.secondary,
  },
  mono: { fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 12.5 },
  ip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    whiteSpace: 'nowrap',
  },
  copy: { padding: 2 },
  bar: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    minWidth: 140,
  },
  track: {
    flex: 1,
    height: 6,
    borderRadius: 999,
    background: theme.palette.action.selected,
    overflow: 'hidden',
  },
  fill: {
    display: 'block',
    height: '100%',
    borderRadius: 999,
    background: theme.palette.primary.main,
  },
  fillHot: { background: theme.palette.status.warning },
  barText: {
    fontSize: 12,
    color: theme.palette.text.secondary,
    whiteSpace: 'nowrap',
  },
  muted: { color: theme.palette.text.secondary },
  foot: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: theme.spacing(1.5),
    fontSize: 12.5,
    color: theme.palette.text.secondary,
    flexWrap: 'wrap',
    gap: theme.spacing(1),
  },
  nowrap: { whiteSpace: 'nowrap' },
  title: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(1.25),
  },
  titleIcon: {
    width: 34,
    height: 34,
    borderRadius: 12,
    background: theme.palette.action.selected,
    color: theme.palette.primary.main,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
}));

/** Заголовок карточки с иконкой Kubernetes, как у таблиц на главной. */
const Title = () => {
  const classes = useStyles();
  return (
    <span className={classes.title}>
      <span className={classes.titleIcon}>
        <KubernetesIcon fontSize="small" />
      </span>
      Узлы
    </span>
  );
};

/** узел, узла, узлов */
/** Подпись-метка группы: мастер-узлы, значение метки или «метки нет». */
const poolTag = (pool: NodePool, label: string): string => {
  if (pool.kind === 'control-plane') return 'control plane';
  return pool.key !== undefined ? `${label}=${pool.key}` : 'метки нет';
};

const nodesWord = (n: number): string => {
  const m10 = n % 10;
  const m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return 'узел';
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return 'узла';
  return 'узлов';
};

const Copy = ({ value }: { value: string }) => {
  const classes = useStyles();
  return (
    <Tooltip title="Скопировать">
      <IconButton
        size="small"
        className={classes.copy}
        aria-label={`Скопировать ${value}`}
        onClick={() => navigator.clipboard?.writeText(value)}
      >
        <FileCopyOutlinedIcon style={{ fontSize: 14 }} />
      </IconButton>
    </Tooltip>
  );
};

const Ip = ({ value }: { value?: string }) => {
  const classes = useStyles();
  if (!value) return <span className={classes.muted}>—</span>;
  return (
    <span className={`${classes.ip} ${classes.mono}`}>
      {value}
      <Copy value={value} />
    </span>
  );
};

const Bar = ({
  used,
  total,
  text,
}: {
  used: number;
  total: number;
  text: string;
}) => {
  const classes = useStyles();
  const pct = total > 0 ? Math.min(100, (used / total) * 100) : 0;
  return (
    <div className={classes.bar}>
      <span className={classes.track}>
        <span
          className={`${classes.fill} ${pct >= 80 ? classes.fillHot : ''}`}
          style={{ width: `${pct}%` }}
        />
      </span>
      <span className={classes.barText}>{text}</span>
    </div>
  );
};

const NodeStatus = ({ node }: { node: NodeRow }) => {
  if (!node.ready) return <StatusError>NotReady</StatusError>;
  if (node.unschedulable)
    return <StatusWarning>SchedulingDisabled</StatusWarning>;
  return <StatusOK>Ready</StatusOK>;
};

const PoolHeader = ({
  pool,
  label,
  open,
  onToggle,
}: {
  pool: NodePool;
  label: string;
  open: boolean;
  onToggle: () => void;
}) => {
  const classes = useStyles();
  const n = pool.nodes.length;
  const allReady = pool.ready === n;
  return (
    <TableRow className={classes.poolRow} onClick={onToggle}>
      <TableCell colSpan={8}>
        <span className={classes.poolName}>
          {open ? (
            <ExpandMoreIcon fontSize="small" />
          ) : (
            <ChevronRightIcon fontSize="small" />
          )}
          {pool.kind === 'control-plane'
            ? 'мастер-узлы'
            : pool.key ?? 'без пула'}
          <span className={classes.tag}>{poolTag(pool, label)}</span>
        </span>
        <span className={classes.facts}>
          <span>
            <span className={classes.factsValue}>{n}</span> {nodesWord(n)}
          </span>
          <span>
            <span className={classes.factsValue}>{formatCores(pool.cpu)}</span>{' '}
            vCPU ·{' '}
            <span className={classes.factsValue}>{formatGiB(pool.memory)}</span>{' '}
            ГБ
            {pool.gpu > 0 && (
              <>
                {' '}
                · <span className={classes.factsValue}>{pool.gpu}</span> GPU
              </>
            )}
          </span>
          {pool.instanceTypes.length > 0 && (
            <span>{pool.instanceTypes.join(', ')}</span>
          )}
          {pool.zones.length > 0 && <span>{pool.zones.join(', ')}</span>}
        </span>
        <span style={{ float: 'right' }}>
          {allReady ? (
            <StatusOK>{`${pool.ready}/${n} Ready`}</StatusOK>
          ) : (
            <StatusError>{`${pool.ready}/${n} Ready`}</StatusError>
          )}
        </span>
      </TableCell>
    </TableRow>
  );
};

const NodeLine = ({ node }: { node: NodeRow }) => {
  const classes = useStyles();
  const roles = [...node.roles, ...(node.roles.length ? [] : ['worker'])];
  return (
    <TableRow>
      <TableCell>
        <span className={classes.nodeName}>{node.name}</span>
        <span className={classes.nodeSub}>
          {[node.os, node.kubelet && `kubelet ${node.kubelet}`]
            .filter(Boolean)
            .join(' · ')}
        </span>
      </TableCell>
      <TableCell>
        <Ip value={node.internalIp} />
      </TableCell>
      <TableCell>
        <Ip value={node.externalIp} />
      </TableCell>
      <TableCell className={classes.nowrap}>
        <NodeStatus node={node} />
      </TableCell>
      <TableCell>
        <span className={classes.muted}>{roles.join(', ')}</span>
        {node.taints.map(t => (
          <Tooltip key={t} title={`taint ${t}`}>
            <span className={classes.tag} style={{ marginLeft: 6 }}>
              {t.split(':')[0]}
            </span>
          </Tooltip>
        ))}
        {node.gpuAllocatable > 0 && (
          <span className={classes.tag} style={{ marginLeft: 6 }}>
            {node.gpuAllocatable} GPU
          </span>
        )}
      </TableCell>
      <TableCell>
        <Bar
          used={node.requests.cpu}
          total={node.cpuAllocatable}
          text={`${formatCores(node.requests.cpu)} / ${formatCores(
            node.cpuAllocatable,
          )} vCPU`}
        />
      </TableCell>
      <TableCell>
        <Bar
          used={node.requests.memory}
          total={node.memoryAllocatable}
          text={`${formatGiB(node.requests.memory)} / ${formatGiB(
            node.memoryAllocatable,
          )} ГБ`}
        />
      </TableCell>
      <TableCell className={`${classes.muted} ${classes.nowrap}`}>
        {node.requests.pods}
        {node.podsAllocatable ? ` / ${node.podsAllocatable}` : ''}
      </TableCell>
    </TableRow>
  );
};

/** Узлы кластера с группировкой по метке; данные — Kubernetes API через плагин Kubernetes. */
export const ClusterNodesCard = () => {
  const classes = useStyles();
  const { entity } = useEntity();
  const domain = annotation(entity, CLUSTER_DOMAIN_ANNOTATION);
  const [label, setLabel] = useState(POOL_LABEL);
  const [query, setQuery] = useState('');
  const [closed, setClosed] = useState<Record<string, boolean>>({});

  const state = useClusterData();
  const rows = state.value?.available ? state.value.nodes : undefined;
  const labels = useMemo(
    () => (rows ? availableGroupLabels(rows) : []),
    [rows],
  );
  const pools = useMemo(
    () =>
      rows
        ? groupNodes(
            rows.filter(n => matchesQuery(n, query)),
            label,
            entity.metadata.annotations ?? {},
          )
        : [],
    [rows, label, query, entity],
  );

  if (state.loading) {
    return (
      <InfoCard title={<Title />}>
        <Progress />
      </InfoCard>
    );
  }
  if (state.error) {
    return (
      <ResponseErrorPanel
        title="Не удалось загрузить узлы"
        error={state.error}
      />
    );
  }
  if (!rows) {
    return (
      <InfoCard title={<Title />}>
        <EmptyState
          missing="info"
          title="Кластер не подключён к плагину Kubernetes"
          description={NOT_CONNECTED}
        />
      </InfoCard>
    );
  }

  const ready = rows.filter(n => n.ready).length;
  const total = rows.length;
  const subheader = [
    `${total} ${nodesWord(total)}`,
    `${ready} Ready`,
    ...(total - ready ? [`${total - ready} NotReady`] : []),
  ].join(' · ');

  return (
    <InfoCard title={<Title />} subheader={subheader}>
      <div className={classes.tools}>
        <TextField
          select
          size="small"
          variant="outlined"
          label="Группировать по"
          value={labels.includes(label) ? label : labels[0] ?? label}
          onChange={e => setLabel(e.target.value)}
          style={{ minWidth: 260 }}
        >
          {(labels.length ? labels : [POOL_LABEL]).map(l => (
            <MenuItem key={l} value={l} className={classes.mono}>
              {l}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          size="small"
          variant="outlined"
          placeholder="имя или IP"
          value={query}
          onChange={e => setQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </div>
      <div style={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Узел</TableCell>
              <TableCell>Внутренний IP</TableCell>
              <TableCell>Внешний IP</TableCell>
              <TableCell>Статус</TableCell>
              <TableCell>Роли</TableCell>
              <TableCell>CPU, запросы</TableCell>
              <TableCell>Память, запросы</TableCell>
              <TableCell>Поды</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pools.map(pool => {
              const id =
                pool.kind === 'control-plane' ? '\u0000cp' : pool.key ?? '';
              const open = !closed[id];
              return [
                <PoolHeader
                  key={`pool-${id}`}
                  pool={pool}
                  label={label}
                  open={open}
                  onToggle={() => setClosed({ ...closed, [id]: open })}
                />,
                ...(open
                  ? pool.nodes.map(n => <NodeLine key={n.name} node={n} />)
                  : []),
              ];
            })}
            {pools.length === 0 && (
              <TableRow>
                <TableCell colSpan={8}>
                  <Typography variant="body2" className={classes.muted}>
                    Ничего не найдено
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <div className={classes.foot}>
        <span>
          Источник: Kubernetes API кластера через GitLab Agent; запросы
          CPU/памяти — сумма requests подов на узле
        </span>
        {domain && (
          <Link to={`https://grafana.${domain}/dashboards?query=Node`}>
            Дашборды узлов в Grafana
          </Link>
        )}
      </div>
    </InfoCard>
  );
};
